// src/services/quoteService.js
import apiClient from './apiClient';

/**
 * Quote service for API operations
 */
export const quoteService = {
  /**
   * Get all quotes with optional filters
   * @param {Object} filters - Optional filters (status, clientId, limit, offset)
   * @returns {Promise<Object>} Response with quotes array
   */
  getAllQuotes: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await apiClient.get(`/quotes?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch quotes');
    }
  },

  /**
   * Get a specific quote by ID
   * @param {string|number} quoteId - The quote ID
   * @returns {Promise<Object>} Response with quote data
   */
  getQuoteById: async (quoteId) => {
    try {
      const response = await apiClient.get(`/quotes/${quoteId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Quote not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch quote');
    }
  },

  /**
   * Create a new quote
   * @param {Object} quoteData - Quote data
   * @returns {Promise<Object>} Response with created quote
   */
  createQuote: async (quoteData) => {
    try {
      const response = await apiClient.post('/quotes', quoteData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create quote');
    }
  },

  /**
   * Update an existing quote
   * @param {string|number} quoteId - The quote ID
   * @param {Object} quoteData - Updated quote data
   * @returns {Promise<Object>} Response with updated quote
   */
  updateQuote: async (quoteId, quoteData) => {
    try {
      const response = await apiClient.put(`/quotes/${quoteId}`, quoteData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update quote');
    }
  },

  /**
   * Delete a quote
   * @param {string|number} quoteId - The quote ID
   * @returns {Promise<Object>} Response confirming deletion
   */
  deleteQuote: async (quoteId) => {
    try {
      const response = await apiClient.delete(`/quotes/${quoteId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete quote');
    }
  },

  /**
   * Accept a quote (client action)
   * @param {string|number} quoteId - The quote ID
   * @returns {Promise<Object>} Response with updated quote
   */
  acceptQuote: async (quoteId) => {
    try {
      const response = await apiClient.post(`/quotes/${quoteId}/accept`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to accept quote');
    }
  },

  /**
   * Reject a quote (client action)
   * @param {string|number} quoteId - The quote ID
   * @returns {Promise<Object>} Response with updated quote
   */
  rejectQuote: async (quoteId) => {
    try {
      const response = await apiClient.post(`/quotes/${quoteId}/reject`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject quote');
    }
  },

  /**
   * Convert quote to invoice
   * @param {string|number} quoteId - The quote ID
   * @returns {Promise<Object>} Response with created invoice
   */
  convertToInvoice: async (quoteId) => {
    try {
      const response = await apiClient.post(`/quotes/${quoteId}/convert-to-invoice`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to convert quote to invoice');
    }
  }
};

export default quoteService;
